import { type Component, createSignal, createMemo, createEffect, onMount, onCleanup, Show } from 'solid-js';
import { useNavigate } from '@solidjs/router';
import { gameStore } from '@/stores/gameStore';
import { chatStore } from '@/stores/chatStore';
import { useTranslation, useLocale } from '@/i18n';
import GameProgress from './GameProgress';
import TeamsProgress from './TeamsProgress';
import TeamChat from './TeamChat';
import GameTimer from './GameTimer';
import StopCodeEntry from './StopCodeEntry';
import LoadingSpinner from './LoadingSpinner';
import PhoneVerificationModal from './PhoneVerificationModal';
import GameEndedModal from './GameEndedModal';
import TeamPhotoUpload from './TeamPhotoUpload';
import MotivationalPopup from './MotivationalPopup';
import SelfserviceRanking from './SelfserviceRanking';
import { selfServiceApi } from '@/services/selfservice';
import type { ComparisonStatsResponse } from '@/services/selfservice';

const Dashboard: Component = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { setLocale } = useLocale();
  const [activeTab, setActiveTab] = createSignal<'game' | 'teams' | 'ranking' | 'chat'>('game');
  const [languageInitialized, setLanguageInitialized] = createSignal(false);
  const [isRefreshing, setIsRefreshing] = createSignal(false);
  const [showStopCodeEntry, setShowStopCodeEntry] = createSignal(false);
  // Local signal for tracking clock stopped in this session (before backend refresh)
  const [localClockStopped, setLocalClockStopped] = createSignal(false);
  // Toast notification for new chat messages
  const [chatToast, setChatToast] = createSignal<string | null>(null);
  // Previous unread count for detecting new messages
  let prevUnreadCount = 0;

  // Motivational popup system (selfservice only)
  const [motivationalStats, setMotivationalStats] = createSignal<ComparisonStatsResponse | null>(null);
  const [showMotivationalPopup, setShowMotivationalPopup] = createSignal(false);
  let lastPopupTime = 0;
  let prevCorrectCount = 0;
  const shownMilestones = new Set<number>();
  const POPUP_COOLDOWN_MS = 180000; // 3 minutes

  // Check if team has finished from backend state (persists across page refresh)
  const isTeamFinished = createMemo(() => {
    // New architecture: check participation.is_finished
    if (gameStore.gameState.architecture === 'new' && gameStore.gameState.participation) {
      return gameStore.gameState.participation.is_finished;
    }
    // Legacy: check team.is_finished
    if (gameStore.gameState.team?.is_finished) {
      return true;
    }
    return false;
  });

  // Combined check: finished from backend OR just stopped locally
  const clockStopped = createMemo(() => isTeamFinished() || localClockStopped());

  // Check if all questions are solved
  const isGameComplete = createMemo(() => {
    const answers = gameStore.gameState.answers;
    const progress = gameStore.gameState.progress;
    if (answers.length === 0) return false;
    const solvedCount = progress.filter(p => p.status === 'correct').length;
    return solvedCount === answers.length;
  });

  // Handle successful clock stop
  const handleClockStopped = () => {
    setLocalClockStopped(true);
    setShowStopCodeEntry(false);
  };

  // Format time to HH:MM:SS (remove microseconds)
  const formatFinalTime = (time: string | null | undefined): string => {
    if (!time) return '--:--:--';
    // Remove microseconds if present (e.g., "00:51:57.4426" -> "00:51:57")
    return time.split('.')[0];
  };

  // Helper functions for architecture-aware status checks
  const isGameActiveForArch = () => {
    if (gameStore.gameState.architecture === 'new' && gameStore.gameState.session) {
      return gameStore.gameState.session.status === 'active';
    }
    return gameStore.gameState.game?.status === 'active';
  };

  const isGameFinishedForArch = () => {
    if (gameStore.gameState.architecture === 'new' && gameStore.gameState.session) {
      return gameStore.gameState.session.status === 'finished';
    }
    return gameStore.gameState.game?.status === 'finished';
  };

  // Check if game session ended (time ran out) but team did NOT finish themselves
  // This triggers the blocking "KONIEC!" modal
  const isSessionEndedByTime = createMemo(() => {
    // Only for new architecture
    if (gameStore.gameState.architecture !== 'new') {
      // Legacy: check if game finished and team didn't finish
      if (gameStore.gameState.game?.status === 'finished' && !isTeamFinished()) {
        return true;
      }
      return false;
    }

    // New architecture: session ended but team didn't complete
    const session = gameStore.gameState.session;
    if (!session) return false;

    // Session is finished AND team has NOT finished themselves
    if (session.status === 'finished' && !isTeamFinished()) {
      return true;
    }

    // Also check if time remaining is 0 or negative and game was active
    if (gameStore.timeRemaining() <= 0 && !isTeamFinished() && !clockStopped()) {
      return true;
    }

    return false;
  });

  // Set language from session's default_language on first load
  createEffect(() => {
    const session = gameStore.gameState.session;
    if (session && !languageInitialized()) {
      const defaultLang = session.default_language;
      if (defaultLang === 'en' || defaultLang === 'pl') {
        setLocale(defaultLang);
        setLanguageInitialized(true);
      }
    }
  });

  // Detect new chat messages and show toast (only if chat enabled)
  createEffect(() => {
    if (!gameStore.gameState.chatEnabled) return;
    const currentCount = chatStore.state.unreadCount;
    // Only show toast if we're not on chat tab and count increased
    if (currentCount > prevUnreadCount && activeTab() !== 'chat') {
      setChatToast(t('chat.newMessage'));
      // Auto-hide after 5 seconds
      setTimeout(() => setChatToast(null), 5000);
    }
    prevUnreadCount = currentCount;
  });

  // Motivational popup trigger - fires when correct answer count changes (selfservice only)
  createEffect(() => {
    if (!isSelfservice()) return;

    const progress = gameStore.gameState.progress;
    const currentCorrect = progress.filter(p => p.status === 'correct').length;
    const totalTiles = gameStore.gameState.answers.length;

    // Skip initial load and zero state
    if (prevCorrectCount === 0 && currentCorrect > 0) {
      prevCorrectCount = currentCorrect;
      return;
    }

    // Only trigger on new correct answers
    if (currentCorrect <= prevCorrectCount) return;
    prevCorrectCount = currentCorrect;

    // Check cooldown
    const now = Date.now();
    if (now - lastPopupTime < POPUP_COOLDOWN_MS) return;

    // Check milestones (25/50/75/100%)
    if (totalTiles > 0) {
      const pct = Math.round((currentCorrect / totalTiles) * 100);
      const milestones = [25, 50, 75, 100];
      for (const m of milestones) {
        if (pct >= m && !shownMilestones.has(m)) {
          shownMilestones.add(m);
          // Force show on milestone regardless of cooldown
          fetchComparisonStats();
          return;
        }
      }
    }

    // Non-milestone: show every few correct answers (after 3rd, 6th, etc.)
    if (currentCorrect % 3 === 0) {
      fetchComparisonStats();
    }
  });

  const fetchComparisonStats = async () => {
    try {
      const sessionId = parseInt(localStorage.getItem('selfservice_session_id') || '0');
      const code = localStorage.getItem('selfservice_access_code') || '';
      if (!sessionId || !code) return;

      const totalDuration = gameStore.gameState.game?.duration;
      const remaining = gameStore.timeRemaining();
      let elapsed = 0;
      if (totalDuration) {
        const parts = totalDuration.split(':').map(Number);
        const totalSec = (parts[0] || 0) * 3600 + (parts[1] || 0) * 60 + (parts[2] || 0);
        elapsed = Math.max(0, totalSec - remaining);
      }

      const correct = gameStore.gameState.progress.filter(p => p.status === 'correct').length;
      const stats = await selfServiceApi.getComparisonStats(sessionId, code, elapsed, correct);

      setMotivationalStats(stats);
      setShowMotivationalPopup(true);
      lastPopupTime = Date.now();
    } catch (err) {
      console.error('Failed to fetch comparison stats:', err);
    }
  };

  let refreshInterval: NodeJS.Timeout | null = null;

  onMount(() => {
    // Load chat unread count on mount
    chatStore.loadUnreadCount();

    // Refresh data every 30 seconds
    refreshInterval = setInterval(async () => {
      if (!isRefreshing() && gameStore.gameState.isAuthenticated) {
        try {
          setIsRefreshing(true);
          await gameStore.loadDashboard();
        } catch (error) {
          console.error('Auto-refresh failed:', error);
        } finally {
          setIsRefreshing(false);
        }
      }
    }, 30000);
  });

  onCleanup(() => {
    if (refreshInterval) {
      clearInterval(refreshInterval);
    }
  });

  // Detect if user came from selfservice flow
  const isSelfservice = () => !!localStorage.getItem('selfservice_access_code');

  const handleLogout = async () => {
    const selfserviceCode = localStorage.getItem('selfservice_access_code');
    await gameStore.logout();
    if (selfserviceCode) {
      // Selfservice users go back to code entry, not login form
      navigate('/selfservice');
    }
  };

  const handleRefresh = async () => {
    if (isRefreshing()) return;

    try {
      setIsRefreshing(true);
      await gameStore.loadDashboard();
    } catch (error) {
      console.error('Manual refresh failed:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Phone verification required check (only for new architecture)
  const needsPhoneVerification = createMemo(() => {
    return gameStore.gameState.architecture === 'new' && !gameStore.gameState.phoneVerified;
  });

  const handlePhoneVerified = () => {
    // Phone verified - modal will close automatically via state change
    gameStore.loadDashboard(); // Refresh to get updated participation data
  };

  return (
    <div class="w-full max-w-4xl mx-auto space-y-6">
      {/* Chat Toast Notification - appears at top when new message arrives */}
      <Show when={chatToast()}>
        <div
          class="fixed top-4 left-1/2 -translate-x-1/2 z-50 animate-slide-down"
          onClick={() => {
            setActiveTab('chat');
            setChatToast(null);
          }}
        >
          <div class="bg-purple-600 text-white px-6 py-3 rounded-xl shadow-lg shadow-purple-500/30 flex items-center gap-3 cursor-pointer hover:bg-purple-500 transition-colors">
            <span class="text-2xl animate-bounce">💬</span>
            <div>
              <div class="font-bold">{chatToast()}</div>
              <div class="text-sm opacity-80">{t('chat.tapToOpen')}</div>
            </div>
          </div>
        </div>
        <style>{`
          @keyframes slide-down {
            from { transform: translate(-50%, -100%); opacity: 0; }
            to { transform: translate(-50%, 0); opacity: 1; }
          }
          .animate-slide-down { animation: slide-down 0.3s ease-out forwards; }
        `}</style>
      </Show>

      {/* Motivational Popup (selfservice only) */}
      <Show when={showMotivationalPopup() && motivationalStats()}>
        <MotivationalPopup
          stats={motivationalStats()!}
          correctAnswers={gameStore.gameState.progress.filter(p => p.status === 'correct').length}
          totalTiles={gameStore.gameState.answers.length}
          onClose={() => setShowMotivationalPopup(false)}
        />
      </Show>

      {/* Game Ended Modal - BLOCKING - shown when session time runs out */}
      <Show when={isSessionEndedByTime()}>
        <GameEndedModal />
      </Show>

      {/* Phone Verification Modal - blocks access until phone is provided */}
      <Show when={needsPhoneVerification()}>
        <PhoneVerificationModal onSuccess={handlePhoneVerified} />
      </Show>

      {/* Header */}
      <div class="card overflow-hidden">
        <div class="card-body relative">
          {/* Game Logo */}
          <Show when={gameStore.gameState.template?.logo_url}>
            <div class="flex justify-center mb-6 pb-4 border-b border-white/10">
              <img
                src={gameStore.gameState.template!.logo_url!}
                alt={gameStore.gameState.template?.name || 'Game Logo'}
                class="h-20 sm:h-28 w-auto object-contain drop-shadow-lg"
              />
            </div>
          </Show>

          {/* Team Name Hero Section */}
          <div class="text-center mb-6 pb-6 border-b border-white/10">
            <p class="text-xs uppercase tracking-[0.3em] text-primary-300/70 mb-2 font-medium">
              {t('dashboard.yourTeamIs')}
            </p>
            <h1 class="relative inline-block">
              <span class="text-4xl sm:text-5xl font-black tracking-tight bg-gradient-to-r from-emerald-400 via-green-400 to-cyan-400 bg-clip-text text-transparent drop-shadow-lg animate-team-glow">
                {gameStore.gameState.team?.name || t('dashboard.title')}
              </span>
              <span class="absolute -inset-2 bg-gradient-to-r from-emerald-400/20 via-green-400/20 to-cyan-400/20 blur-xl rounded-lg -z-10"></span>
            </h1>
            <style>{`
              @keyframes team-glow {
                0%, 100% { filter: drop-shadow(0 0 8px rgba(52, 211, 153, 0.4)); }
                50% { filter: drop-shadow(0 0 16px rgba(52, 211, 153, 0.6)); }
              }
              .animate-team-glow { animation: team-glow 2s ease-in-out infinite; }
            `}</style>
          </div>

          {/* Game Info & Actions Row */}
          <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <p class="text-primary-200 text-sm">
                {/* Display session info based on architecture */}
                {gameStore.gameState.architecture === 'new' && gameStore.gameState.session ? (
                  <>
                    <span class="font-semibold text-white">{gameStore.gameState.session.template_name}</span>
                    <span class="mx-2 text-primary-400">•</span>
                    {gameStore.gameState.session.template_location}
                    <span class="text-primary-400 ml-2">
                      ({gameStore.gameState.session.client_name})
                    </span>
                  </>
                ) : (
                  <>
                    <span class="font-semibold text-white">{gameStore.gameState.game?.name}</span>
                    <span class="mx-2 text-primary-400">•</span>
                    {gameStore.gameState.game?.location}
                  </>
                )}
              </p>
            </div>

            <div class="flex items-center gap-3" role="toolbar" aria-label="Dashboard actions">
              <button
                onClick={handleRefresh}
                class="btn btn-outline btn-sm"
                disabled={isRefreshing()}
                title={t('common.refresh')}
                aria-label={t('common.refresh')}
                aria-busy={isRefreshing()}
              >
                {isRefreshing() ? (
                  <LoadingSpinner size="sm" color="primary" />
                ) : (
                  '🔄'
                )}
              </button>

              <button
                onClick={handleLogout}
                class="btn btn-outline btn-sm"
                title={isSelfservice() ? 'Powrót do kodu' : t('dashboard.logout')}
                aria-label={isSelfservice() ? 'Powrót do kodu' : t('dashboard.logout')}
              >
                {isSelfservice() ? '🔙 Powrót' : `🚪 ${t('dashboard.logout')}`}
              </button>
            </div>
          </div>

          {/* Game Timer - always visible */}
          <div class="mt-6">
            <GameTimer />
          </div>

          {/* Game Status */}
          <div class="mt-4 text-center">
            <Show when={isGameActiveForArch() && gameStore.isGameActive()}>
              <div class="flex items-center justify-center gap-2 text-secondary-color">
                <span class="w-2 h-2 bg-secondary-color rounded-full animate-pulse"></span>
                <span class="font-medium">{t('dashboard.gameActive')}</span>
              </div>
            </Show>

            <Show when={isGameFinishedForArch() || !gameStore.isGameActive()}>
              <div class="flex items-center justify-center gap-2 text-primary-300">
                <span class="w-2 h-2 bg-primary-400 rounded-full"></span>
                <span class="font-medium">{t('dashboard.gameFinished')}</span>
              </div>
            </Show>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div class="card">
        <div class="card-header">
          <div class="flex border-b border-primary-600/50" role="tablist" aria-label={t('dashboard.title')}>
            <button
              onClick={() => setActiveTab('game')}
              class={`px-4 py-3 font-medium text-sm border-b-2 transition-colors ${
                activeTab() === 'game'
                  ? 'border-secondary-400 text-secondary-400'
                  : 'border-transparent text-primary-300 hover:text-white'
              }`}
              role="tab"
              aria-selected={activeTab() === 'game'}
              aria-controls="tabpanel-game"
              id="tab-game"
            >
              🎮 {t('dashboard.tabs.game')}
            </button>

            {/* Selfservice: show Ranking tab; Non-selfservice: show Teams tab */}
            <Show when={isSelfservice()} fallback={
              <button
                onClick={() => setActiveTab('teams')}
                class={`px-4 py-3 font-medium text-sm border-b-2 transition-colors ${
                  activeTab() === 'teams'
                    ? 'border-secondary-400 text-secondary-400'
                    : 'border-transparent text-primary-300 hover:text-white'
                }`}
                role="tab"
                aria-selected={activeTab() === 'teams'}
                aria-controls="tabpanel-teams"
                id="tab-teams"
              >
                👥 {t('teamsProgress.title')}
              </button>
            }>
              <button
                onClick={() => setActiveTab('ranking')}
                class={`px-4 py-3 font-medium text-sm border-b-2 transition-colors ${
                  activeTab() === 'ranking'
                    ? 'border-secondary-400 text-secondary-400'
                    : 'border-transparent text-primary-300 hover:text-white'
                }`}
                role="tab"
                aria-selected={activeTab() === 'ranking'}
                aria-controls="tabpanel-ranking"
                id="tab-ranking"
              >
                🏆 {t('dashboard.tabs.ranking')}
              </button>
            </Show>

            <Show when={gameStore.gameState.chatEnabled}>
              <button
                onClick={() => setActiveTab('chat')}
                class={`px-4 py-3 font-medium text-sm border-b-2 transition-colors relative ${
                  activeTab() === 'chat'
                    ? 'border-secondary-400 text-secondary-400'
                    : 'border-transparent text-primary-300 hover:text-white'
                }`}
                role="tab"
                aria-selected={activeTab() === 'chat'}
                aria-controls="tabpanel-chat"
                id="tab-chat"
              >
                💬 {t('dashboard.tabs.chat')}
                <Show when={chatStore.state.unreadCount > 0}>
                  <span class="absolute -top-1 -right-1 min-w-[20px] h-5 px-1.5 flex items-center justify-center bg-red-500 text-white text-xs font-bold rounded-full animate-pulse">
                    {chatStore.state.unreadCount > 9 ? '9+' : chatStore.state.unreadCount}
                  </span>
                </Show>
              </button>
            </Show>
          </div>
        </div>

        <div class="card-body">
          <Show when={activeTab() === 'game'}>
            <div
              class="space-y-6"
              role="tabpanel"
              id="tabpanel-game"
              aria-labelledby="tab-game"
            >
              <GameProgress />
            </div>
          </Show>

          <Show when={activeTab() === 'teams'}>
            <div
              role="tabpanel"
              id="tabpanel-teams"
              aria-labelledby="tab-teams"
            >
              <TeamsProgress />
            </div>
          </Show>

          <Show when={activeTab() === 'ranking'}>
            <div
              role="tabpanel"
              id="tabpanel-ranking"
              aria-labelledby="tab-ranking"
            >
              <SelfserviceRanking />
            </div>
          </Show>

          <Show when={activeTab() === 'chat' && gameStore.gameState.chatEnabled}>
            <div
              role="tabpanel"
              id="tabpanel-chat"
              aria-labelledby="tab-chat"
            >
              <TeamChat />
            </div>
          </Show>
        </div>
      </div>

      {/* Stop Code Entry Modal */}
      <Show when={showStopCodeEntry() && !clockStopped()}>
        <StopCodeEntry
          onSuccess={handleClockStopped}
          onClose={() => setShowStopCodeEntry(false)}
        />
      </Show>

      {/* STOP CLOCK button - appears when all puzzles are solved BUT team has not finished yet */}
      <Show when={isGameComplete() && !clockStopped() && !showStopCodeEntry()}>
        <div class="card mt-6">
          <div class="card-body">
            <div class="text-center space-y-4">
              <div class="text-5xl animate-bounce">🎉</div>
              <h3 class="text-2xl font-bold text-emerald-400">
                {t('dashboard.allPuzzlesSolved')}
              </h3>
              <p class="text-white/70">
                {t('dashboard.writeDigits')}
              </p>
              <button
                onClick={() => setShowStopCodeEntry(true)}
                class="px-8 py-4 rounded-xl font-bold text-xl text-white bg-gradient-to-r from-emerald-500 to-cyan-500 shadow-lg shadow-emerald-500/30 hover:shadow-emerald-500/50 hover:scale-105 transition-all"
              >
                ⏱️ {t('dashboard.stopClock')}
              </button>
            </div>
          </div>
        </div>
      </Show>

      {/* GAME COMPLETED - shown when team has finished (blocks further interaction) */}
      <Show when={clockStopped() && !showStopCodeEntry()}>
        <div class="card mt-6 border-2 border-emerald-500/30">
          <div class="card-body">
            <div class="text-center space-y-6">
              {/* Trophy animation */}
              <div class="relative inline-block">
                <div class="text-7xl animate-bounce">🎉</div>
                <div class="absolute -inset-4 bg-emerald-500/20 rounded-full blur-xl -z-10" />
              </div>

              <div>
                <h3 class="text-3xl font-black bg-gradient-to-r from-emerald-400 via-cyan-400 to-green-400 bg-clip-text text-transparent mb-2">
                  🎉 {t('dashboard.congratulations')} 🎉
                </h3>
                <p class="text-xl text-white/80 font-semibold">
                  {gameStore.gameState.team?.name}, {t('dashboard.youCompletedGame')}
                </p>
              </div>

              {/* Final time display */}
              <Show when={gameStore.gameState.participation?.final_game_time}>
                <div class="bg-black/30 rounded-2xl border border-emerald-500/30 p-6 max-w-xs mx-auto">
                  <div class="text-sm text-white/50 uppercase tracking-wider mb-2">
                    {t('dashboard.yourTime')}
                  </div>
                  <div class="text-4xl font-black text-emerald-400 font-mono tracking-wider">
                    {formatFinalTime(gameStore.gameState.participation?.final_game_time)}
                  </div>
                </div>
              </Show>

              {/* Celebration message */}
              <div class="bg-emerald-500/10 rounded-xl p-4 max-w-sm mx-auto">
                <p class="text-emerald-300/80 text-sm">
                  {t('dashboard.waitForAnnouncement')}
                </p>
              </div>

              {/* Confetti particles */}
              <div class="flex justify-center gap-2 text-2xl">
                <span class="animate-bounce" style="animation-delay: 0ms">🎊</span>
                <span class="animate-bounce" style="animation-delay: 100ms">✨</span>
                <span class="animate-bounce" style="animation-delay: 200ms">🎉</span>
                <span class="animate-bounce" style="animation-delay: 300ms">✨</span>
                <span class="animate-bounce" style="animation-delay: 400ms">🎊</span>
              </div>
            </div>
          </div>
        </div>
      </Show>

      {/* Team Photo Upload - always at the bottom for new architecture */}
      <Show when={gameStore.gameState.architecture === 'new'}>
        <TeamPhotoUpload />
      </Show>

      {/* Client Logo - always the LAST element on screen */}
      <Show when={gameStore.gameState.session?.client_logo_url}>
        <div class="mt-8 pt-6 border-t border-white/10">
          <div class="flex flex-col items-center gap-3">
            {/* Optional title - only shown if provided */}
            <Show when={gameStore.gameState.session?.client_logo_title}>
              <p class="text-xs uppercase tracking-widest text-white/40 font-medium">
                {gameStore.gameState.session!.client_logo_title}
              </p>
            </Show>
            {/* Client logo */}
            <img
              src={gameStore.gameState.session!.client_logo_url!}
              alt={gameStore.gameState.session?.client_name || 'Organizator'}
              class="h-12 sm:h-16 w-auto object-contain opacity-70 hover:opacity-100 transition-opacity"
            />
          </div>
        </div>
      </Show>
    </div>
  );
};

export default Dashboard;
